import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useCollectionStore } from '../../lib/collectionStore';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { useCurrencyStore } from '../../lib/currencyStore';
import { formatPrice } from '../../utils/formatPrice';
import TopPortfolioCards from '../../components/TopPortfolioCards';
import { fetchScrydexNmPrices } from '../../utils/cardPricing';

interface PortfolioHistoryEntry {
  created_at: string;
  total_value: number;
  unique_cards: number;
  total_cards: number;
}

interface TopCard {
  id: string;
  name: string;
  number: string;
  set: {
    name: string;
    series: string;
  };
  types?: string[];
  market_price_trend_usd: number;
  quantity: number;
}

type TimeFrame = '1D' | '7D' | '1M' | '3M' | '6M' | 'MAX';

export default function PortfolioView() {
  const [history, setHistory] = useState<PortfolioHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currency } = useCurrencyStore();
  const { collection } = useCollectionStore();
  const [timeFrame, setTimeFrame] = useState<TimeFrame>('1M');
  const [topCards, setTopCards] = useState<TopCard[]>([]);

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        setLoading(true);

        // Calculate the start date based on time frame
        const now = new Date();
        let startDate = new Date();
        switch (timeFrame) {
          case '1D':
            startDate.setDate(now.getDate() - 1);
            break;
          case '7D':
            startDate.setDate(now.getDate() - 7);
            break;
          case '1M':
            startDate.setMonth(now.getMonth() - 1);
            break;
          case '3M':
            startDate.setMonth(now.getMonth() - 3);
            break;
          case '6M':
            startDate.setMonth(now.getMonth() - 6);
            break;
          case 'MAX':
            startDate = new Date(0); // Beginning of time
            break;
        }

        // Fetch portfolio history
        const { data: historyData, error: historyError } = await supabase
          .from('portfolio_history')
          .select('*')
          .gte('created_at', startDate.toISOString())
          .order('created_at', { ascending: true });

        if (historyError) throw historyError;

        // For 7D+ timeframes, keep only the latest record per day
        const filteredHistory = timeFrame === '1D'
          ? historyData
          : Object.values(
              (historyData || []).reduce<Record<string, PortfolioHistoryEntry>>((acc, entry) => {
                const day = entry.created_at.slice(0, 10);
                if (!acc[day] || entry.created_at > acc[day].created_at) {
                  acc[day] = entry;
                }
                return acc;
              }, {})
            ).sort((a, b) => a.created_at.localeCompare(b.created_at));

        // Fetch all cards in collection with their prices
        const { data: cardsData, error: cardsError } = await supabase
          .from('collections')
          .select(`
            card_id,
            quantity,
            card_finish,
            pokemontcg_io (
              id,
              name,
              number,
              types,
              market_price_trend_usd,
              set_name,
              series
            )
          `)
          .limit(50000);

        if (cardsError) throw cardsError;

        // Fetch Scrydex NM prices for all collection cards
        const cardIds = cardsData.map(item => item.card_id);
        const scrydexPrices = await fetchScrydexNmPrices(cardIds);

        const transformedCards = cardsData
          .map(item => {
            const scrydexPrice = scrydexPrices.get(item.card_id);
            const individualPrice = scrydexPrice || item.pokemontcg_io.market_price_trend_usd || 0;
            return {
              id: item.card_id,
              name: item.pokemontcg_io.name,
              number: item.pokemontcg_io.number,
              set: {
                name: item.pokemontcg_io.set_name,
                series: item.pokemontcg_io.series
              },
              types: item.pokemontcg_io.types,
              market_price_trend_usd: individualPrice,
              quantity: item.quantity,
              totalValue: individualPrice * item.quantity,
              individualPrice: individualPrice
            };
          })
          .sort((a, b) => b.individualPrice - a.individualPrice)
          .slice(0, 5);

        setHistory(filteredHistory || []);
        setTopCards(transformedCards);
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load portfolio data');
      } finally {
        setLoading(false);
      }
    };

    fetchPortfolioData();
  }, [collection, timeFrame]);

  const currentValue = history[history.length - 1]?.total_value || 0;
  const startValue = history[0]?.total_value || 0;
  const valueChange = currentValue - startValue;
  const percentChange = startValue > 0 ? (valueChange / startValue) * 100 : 0;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (timeFrame === '1D') {
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(timeFrame === '6M' && { year: '2-digit' })
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 text-red-500 p-4 rounded-lg">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold">Portfolio Value</h2>
          
          {/* Time frame selector */}
          <div className="flex items-center gap-1">
            {(['1D', '7D', '1M', '3M', '6M', 'MAX'] as TimeFrame[]).map((frame) => (
              <button
                key={frame}
                onClick={() => setTimeFrame(frame)}
                className={`px-3 py-1.5 rounded-lg transition-colors ${
                  timeFrame === frame 
                    ? 'bg-white/20 text-white' 
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {frame}
              </button>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/5 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-white/60 mb-1">
              <DollarSign size={16} />
              <span className="text-sm">Current Value</span>
            </div>
            <p className="text-2xl font-bold">{formatPrice(currentValue, currency)}</p>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-white/60 mb-1">
              <TrendingUp size={16} />
              <span className="text-sm">{timeFrame} Change</span>
            </div>
            <p className={`text-2xl font-bold ${valueChange >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {valueChange >= 0 ? '+' : ''}{formatPrice(valueChange, currency)}
              <span className="text-sm ml-1">({percentChange.toFixed(2)}%)</span>
            </p>
          </div>
          
          <div className="bg-white/5 p-4 rounded-lg">
            <div className="flex items-center gap-2 text-white/60 mb-1">
              <Calendar size={16} />
              <span className="text-sm">Last Updated</span>
            </div>
            <p className="text-lg">
              {history.length > 0 ? new Date(history[history.length - 1].created_at).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>

        <div className="h-[500px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={history} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
              <defs>
                <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22C55E" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#22C55E" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                stroke="rgba(255,255,255,0.1)" 
                vertical={false}
              />
              <XAxis 
                dataKey="created_at" 
                tickFormatter={formatDate}
                stroke="rgba(255,255,255,0.5)"
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                interval="preserveStartEnd"
                minTickGap={50}
              />
              <YAxis 
                domain={[0, 'auto']}
                tickFormatter={(value) => formatPrice(value, currency)}
                stroke="rgba(255,255,255,0.5)"
                tickLine={false}
                axisLine={false}
                tickMargin={12}
                width={100}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'rgba(0,0,0,0.8)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '8px',
                  padding: '12px'
                }}
                labelFormatter={(value) => new Date(value).toLocaleString()}
                formatter={(value: any) => [formatPrice(value, currency), 'Portfolio Value']}
                labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
              />
              <Area
                type="monotone"
                dataKey="total_value"
                stroke="#22C55E"
                fillOpacity={1}
                fill="url(#colorValue)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, fill: '#22C55E', stroke: '#000', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top 5 Most Valuable Cards */}
      {topCards.length > 0 && (
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <h3 className="text-lg font-bold mb-4">
            Your Most Valuable Cards <span className="text-red-500 animate-heartbeat">♥</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <TopPortfolioCards cards={topCards} />
          </div>
        </div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-lg p-4">
        <h3 className="text-lg font-bold mb-4">Portfolio Statistics</h3>
        <div className="space-y-2">
          <p className="text-white/60">
            Total Cards: {history[history.length - 1]?.total_cards || 0}
          </p>
          <p className="text-white/60">
            Unique Cards: {history[history.length - 1]?.unique_cards || 0}
          </p>
          <p className="text-white/60">
            Average Card Value: {formatPrice((currentValue / (history[history.length - 1]?.total_cards || 1)), currency)}
          </p>
        </div>
      </div>
    </div>
  );
}